/**
 * DataPreviewValidationModal
 *
 * Shows the result of a /validateData call before a GeoLift CSV is uploaded
 * to S3: full-column validation errors (blocking), location-format warnings
 * (require acknowledgement), and a start/middle/end sample of the parsed
 * rows so the user can see exactly what the pipeline will read from their
 * file - not just the first few rows, which can miss issues (e.g. a footer
 * "Total" row, or a format that only breaks partway through the file).
 */

import React, { useEffect, useState } from "react";
import { Modal, Table, Checkbox, Divider } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { GeoLiftDataValidationResponse } from "../utils/dataValidation";
import CustomExcelIcon from "../assets/icons/CustomExcelIcon";

// Chip used for the location-format breakdown (states / DMA codes / etc.) -
// styled with the app's semantic Tailwind tokens (tailwind.config.js) rather
// than antd Tag's default palette, so it matches the rest of the page. The
// "primary" tone uses blue-600 (not the `primary` brand token, #0055B8) to
// match the blue-500/600 already used for this modal's own icon/accents -
// mixing in the brand token here would introduce a second, different blue.
const StatusChip: React.FC<{
  tone: "success" | "primary" | "error";
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const toneClasses = {
    success: "bg-success-50 text-success-700 border-success-100",
    primary: "bg-blue-50 text-blue-600 border-blue-100",
    error: "bg-error-50 text-error-700 border-error-100",
  }[tone];

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${toneClasses}`}
    >
      {children}
    </span>
  );
};

interface DataPreviewValidationModalProps {
  visible: boolean;
  loading?: boolean;
  fileName?: string;
  validationResponse: GeoLiftDataValidationResponse | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLoading?: boolean;
}

const ZONE_LABELS: { key: "start" | "middle" | "end"; label: string }[] = [
  { key: "start", label: "Start of file" },
  { key: "middle", label: "Middle of file" },
  { key: "end", label: "End of file" },
];

const DataPreviewValidationModal: React.FC<DataPreviewValidationModalProps> = ({
  visible,
  loading = false,
  fileName,
  validationResponse,
  onConfirm,
  onCancel,
  confirmLoading = false,
}) => {
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);

  useEffect(() => {
    setAcknowledgedWarnings(false);
  }, [validationResponse]);

  const hasErrors = (validationResponse?.errors?.length ?? 0) > 0;
  // Per user request: only flag actual problems here, not "this is fine, just
  // informing you" notices. Hidden warning types (still computed and returned
  // by /validateData on the backend, just not displayed):
  // - "no_control_market": national-buy case, not something the user can fix
  //   on this screen.
  // - "location_needs_geocoding": locations that ARE in valid 'City, State'
  //   format and will resolve fine, just informing that geocoding takes a
  //   moment - not a problem to flag.
  // "location_unrecognized_format" (locations that don't match any known
  // format at all) is a genuine problem and stays visible.
  const HIDDEN_WARNING_TYPES = new Set(["no_control_market", "location_needs_geocoding"]);
  const displayWarnings = (validationResponse?.warnings ?? []).filter(
    (w) => !HIDDEN_WARNING_TYPES.has(w.type)
  );
  const hasWarnings = displayWarnings.length > 0;
  const canConfirm =
    !loading &&
    !!validationResponse &&
    validationResponse.isValid &&
    (!hasWarnings || acknowledgedWarnings);

  const columns = validationResponse?.preview?.columns ?? [];
  const tableColumns = [
    {
      title: "Zone",
      dataIndex: "__zone",
      key: "__zone",
      fixed: "left" as const,
      render: (zone: string, row: any) => {
        // Only show the zone label on the first row of each zone group
        return { children: zone, props: { rowSpan: row.__zoneRowSpan ?? 1 } };
      },
    },
    ...columns.map((col) => {
      const isDecimalColumn = ["y", "adspend"].includes(col.toLowerCase());
      return {
        title: col,
        dataIndex: col,
        key: col,
        render: (val: string | null) => {
          if (val === null || val === undefined || val === "") {
            return <span className="text-gray-400 italic text-xs">(blank)</span>;
          }
          if (isDecimalColumn) {
            const num = Number(val);
            return Number.isFinite(num) ? num.toFixed(2) : val;
          }
          return val;
        },
      };
    }),
  ];

  const buildPreviewRows = () => {
    if (!validationResponse) return [];
    const rows: any[] = [];
    ZONE_LABELS.forEach(({ key, label }) => {
      const zoneRows = validationResponse.preview.sampleZones[key] ?? [];
      zoneRows.forEach((row, idx) => {
        rows.push({
          key: `${key}-${idx}`,
          __zone: label,
          __zoneRowSpan: idx === 0 ? zoneRows.length : 0,
          ...row,
        });
      });
    });
    return rows;
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-3 py-1">
          <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
            <CustomExcelIcon className="text-white text-lg" />
          </div>
          <span className="text-base font-semibold text-gray-900">
            Review Uploaded Data
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      okText="Looks good, continue"
      okButtonProps={{ disabled: !canConfirm, loading: confirmLoading }}
      onOk={onConfirm}
      cancelText="Cancel"
    >
      {loading && (
        <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-gray-700">
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
          Validating file...
        </div>
      )}

      {!loading && validationResponse && (
        <div className="space-y-4">
          {fileName && (
            <p className="text-sm text-gray-500 m-0">
              File: <strong className="text-gray-700">{fileName}</strong>
              {validationResponse.summary.totalRows !== undefined &&
                ` · ${validationResponse.summary.totalRows} rows · ${
                  validationResponse.summary.uniqueLocations ?? "?"
                } unique locations`}
            </p>
          )}

          {hasErrors && (
            <div className="rounded-xl border border-error-100 bg-error-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CloseCircleOutlined className="text-error-600" />
                <span className="text-sm font-semibold text-error-700">
                  This file can't be processed yet
                </span>
              </div>
              <ul className="list-disc pl-6 mb-0 space-y-1.5 text-sm text-gray-700">
                {validationResponse.errors.map((err, i) => (
                  <li key={i}>
                    {err.message}
                    {err.examples && err.examples.length > 0 && (
                      <div className="text-xs mt-1 text-gray-500">
                        Example row(s):{" "}
                        {err.examples
                          .slice(0, 5)
                          .map((ex) => `row ${ex.rowIndex} ("${ex.value ?? "blank"}")`)
                          .join(", ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasWarnings && (
            <div className="rounded-xl border border-warning-100 bg-warning-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <WarningOutlined className="text-warning-600" />
                <span className="text-sm font-semibold text-warning-700">
                  Some locations may need extra processing
                </span>
              </div>
              <ul className="list-disc pl-6 mb-3 space-y-1.5 text-sm text-gray-700">
                {displayWarnings.map((w, i) => (
                  <li key={i}>
                    {w.message}
                    {w.examples.length > 0 && (
                      <div className="text-xs mt-1 text-gray-500">
                        Examples: {w.examples.slice(0, 5).join(", ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <Checkbox
                checked={acknowledgedWarnings}
                onChange={(e) => setAcknowledgedWarnings(e.target.checked)}
              >
                <span className="text-sm text-gray-700">
                  I understand and want to proceed anyway
                </span>
              </Checkbox>
            </div>
          )}

          {!hasErrors && !hasWarnings && (
            <div className="flex items-center gap-2 rounded-xl border border-success-100 bg-success-50 p-4">
              <CheckCircleOutlined className="text-success-600" />
              <span className="text-sm font-semibold text-success-700">
                All checks passed
              </span>
            </div>
          )}

          {validationResponse.summary.locationClassification && (
            <div className="flex flex-wrap gap-2">
              <StatusChip tone="success">
                States: {validationResponse.summary.locationClassification.state}
              </StatusChip>
              <StatusChip tone="success">
                State codes: {validationResponse.summary.locationClassification.state_abbrev}
              </StatusChip>
              <StatusChip tone="success">
                DMA codes: {validationResponse.summary.locationClassification.dma_code}
              </StatusChip>
              <StatusChip tone="primary">
                City, State: {validationResponse.summary.locationClassification.city_state}
              </StatusChip>
              {validationResponse.summary.locationClassification.unrecognized > 0 && (
                <StatusChip tone="error">
                  Unrecognized: {validationResponse.summary.locationClassification.unrecognized}
                </StatusChip>
              )}
            </div>
          )}

          {columns.length > 0 && (
            <>
              <Divider className="my-3" />
              <h4 className="text-base font-semibold text-gray-800 mb-2">
                Sample rows (start / middle / end of file)
              </h4>
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <Table
                  size="small"
                  columns={tableColumns as any}
                  dataSource={buildPreviewRows()}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DataPreviewValidationModal;
